/**
 * React Query hooks for AI training and feedback
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getRecentConversations,
  getTrainingExamples,
  getTrainingStats,
  createTrainingExample,
  updateTrainingExample,
  deleteTrainingExample,
  toggleTrainingExampleActive,
} from "@/data/aiTraining";
import { toast } from "sonner";

const CACHE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch recent conversations for review
 */
export function useRecentConversations(limit = 50) {
  return useQuery({
    queryKey: ['ai-training-conversations', limit],
    queryFn: () => getRecentConversations(limit),
    staleTime: CACHE_TIME,
    gcTime: CACHE_TIME * 2,
  });
}

/**
 * Fetch training examples with optional filters
 */
export function useTrainingExamples(filters?: {
  rating?: 'good' | 'bad';
  category?: string;
  is_active?: boolean;
}) {
  return useQuery({
    queryKey: ['ai-training-examples', filters],
    queryFn: () => getTrainingExamples(filters),
    staleTime: CACHE_TIME,
    gcTime: CACHE_TIME * 2,
  });
}

/**
 * Fetch training statistics
 */
export function useTrainingStats() {
  return useQuery({
    queryKey: ['ai-training-stats'],
    queryFn: getTrainingStats,
    staleTime: CACHE_TIME,
    gcTime: CACHE_TIME * 2,
  });
}

/**
 * Create a new training example
 */
export function useCreateTrainingExample() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTrainingExample,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-training-examples'] });
      queryClient.invalidateQueries({ queryKey: ['ai-training-stats'] });
      toast.success("Training example saved");
    },
    onError: (error: Error) => {
      toast.error(`Failed to save training example: ${error.message}`);
    },
  });
}

/**
 * Update a training example
 */
export function useUpdateTrainingExample() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any }) =>
      updateTrainingExample(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-training-examples'] });
      queryClient.invalidateQueries({ queryKey: ['ai-training-stats'] });
      toast.success("Training example updated");
    },
    onError: (error: Error) => {
      toast.error(`Failed to update training example: ${error.message}`);
    },
  });
}

/**
 * Delete a training example
 */
export function useDeleteTrainingExample() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTrainingExample,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-training-examples'] });
      queryClient.invalidateQueries({ queryKey: ['ai-training-stats'] });
      toast.success("Training example deleted");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete training example: ${error.message}`);
    },
  });
}

/**
 * Toggle active status of a training example
 */
export function useToggleTrainingExampleActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      toggleTrainingExampleActive(id, is_active),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-training-examples'] });
      queryClient.invalidateQueries({ queryKey: ['ai-training-stats'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to toggle active status: ${error.message}`);
    },
  });
}

/**
 * Knowledge Base hooks
 */
export function useKnowledgeBases() {
  return useQuery({
    queryKey: ['ai-knowledge-bases'],
    queryFn: async () => {
      const { getKnowledgeBases } = await import("@/data/aiTraining");
      return getKnowledgeBases();
    },
    staleTime: CACHE_TIME,
    gcTime: CACHE_TIME * 2,
  });
}

export function useKnowledgeItems(knowledgeBaseId: string | null) {
  return useQuery({
    queryKey: ['ai-knowledge-items', knowledgeBaseId],
    queryFn: async () => {
      if (!knowledgeBaseId) return [];
      const { getKnowledgeItems } = await import("@/data/aiTraining");
      return getKnowledgeItems(knowledgeBaseId);
    },
    enabled: !!knowledgeBaseId,
    staleTime: CACHE_TIME,
    gcTime: CACHE_TIME * 2,
  });
}

export function useCreateKnowledgeBase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, type }: { name: string; type: string }) => {
      const { createKnowledgeBase } = await import("@/data/aiTraining");
      return createKnowledgeBase(name, type);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-knowledge-bases'] });
      toast.success("Knowledge base created");
    },
    onError: (error: Error) => {
      toast.error(`Failed to create knowledge base: ${error.message}`);
    },
  });
}

export function useCreateKnowledgeItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (item: any) => {
      const { createKnowledgeItem } = await import("@/data/aiTraining");
      return createKnowledgeItem(item);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-knowledge-items'] });
      queryClient.invalidateQueries({ queryKey: ['ai-knowledge-bases'] });
      toast.success("Knowledge item added");
    },
    onError: (error: Error) => {
      toast.error(`Failed to add knowledge item: ${error.message}`);
    },
  });
}

export function useDeleteKnowledgeItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { deleteKnowledgeItem } = await import("@/data/aiTraining");
      return deleteKnowledgeItem(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-knowledge-items'] });
      queryClient.invalidateQueries({ queryKey: ['ai-knowledge-bases'] });
      toast.success("Knowledge item deleted");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete knowledge item: ${error.message}`);
    },
  });
}

export function useDeleteKnowledgeBase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { deleteKnowledgeBase } = await import("@/data/aiTraining");
      return deleteKnowledgeBase(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-knowledge-bases'] });
      toast.success("Knowledge base deleted");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete knowledge base: ${error.message}`);
    },
  });
}

/**
 * Bot Settings hooks
 */
export function useBotSettings() {
  return useQuery({
    queryKey: ['ai-bot-settings'],
    queryFn: async () => {
      const { getBotSettings } = await import("@/data/aiTraining");
      return getBotSettings();
    },
    staleTime: CACHE_TIME,
    gcTime: CACHE_TIME * 2,
  });
}

export function useUpdateBotSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: any) => {
      const { updateBotSettings } = await import("@/data/aiTraining");
      return updateBotSettings(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-bot-settings'] });
      toast.success("Bot settings updated");
    },
    onError: (error: Error) => {
      toast.error(`Failed to update bot settings: ${error.message}`);
    },
  });
}
